import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, Trash2, MessageSquare, Send } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { BookingStatusBadge } from '@/components/bookings/BookingStatusBadge'
import { BookingForm } from '@/components/bookings/BookingForm'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Modal } from '@/components/ui/Modal'
import { Skeleton } from '@/components/ui/Skeleton'
import { useBooking } from '@/lib/queries/useBookings'
import { useBookingActions } from '@/lib/hooks/useBookingActions'
import { useCreateComment } from '@/lib/queries/useComments'
import { getStatusActions, STATUS_CONFIG } from '@/lib/utils/booking'
import { formatDate, formatRelative } from '@/lib/utils/date'
import { fadeInUp } from '@/lib/motion'
import type { BookingStatus } from '@/lib/types/booking'

export default function BookingDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: resp, isLoading } = useBooking(id!)
  const bookingActions = useBookingActions()
  const createComment = useCreateComment()
  const [showEdit, setShowEdit] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [note, setNote] = useState('')
  const [commentText, setCommentText] = useState('')
  const [savingNote, setSavingNote] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const booking = resp?.data

  useEffect(() => { if (booking) setNote(booking.note ?? '') }, [booking?.documentId])

  if (isLoading || !booking) {
    return (
      <div className="space-y-4">
        <Skeleton variant="rectangular" height={40} width={200} />
        <Skeleton variant="rectangular" height={200} />
        <Skeleton variant="rectangular" height={200} />
      </div>
    )
  }

  const actions = getStatusActions(booking.status)

  return (
    <div className="max-w-3xl">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon-sm" onClick={() => navigate('/bookings')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl font-bold text-[var(--color-text-primary)]">
              {booking.guest?.name ?? 'Walk-in'}
            </h1>
            <BookingStatusBadge status={booking.status} />
          </div>
          <p className="text-sm text-[var(--color-text-muted)] mt-0.5">
            Created {formatRelative(booking.createdAt)}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setShowEdit(true)}>Edit</Button>
      </div>

      <div className="space-y-4">
        {/* Booking Info */}
        <motion.div {...fadeInUp}>
          <Card>
            <CardHeader><CardTitle>Booking Details</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-[var(--color-text-muted)]">Date</span><p className="font-medium">{formatDate(booking.date)}</p></div>
                <div><span className="text-[var(--color-text-muted)]">Time</span><p className="font-medium">{booking.time}</p></div>
                <div><span className="text-[var(--color-text-muted)]">Party Size</span><p className="font-medium">{booking.people} guests</p></div>
                <div><span className="text-[var(--color-text-muted)]">Duration</span><p className="font-medium">{booking.duration} min</p></div>
                <div><span className="text-[var(--color-text-muted)]">Source</span><p className="font-medium capitalize">{booking.source}</p></div>
                <div><span className="text-[var(--color-text-muted)]">Tables</span><p className="font-medium">{booking.bookingTables?.map((bt) => bt.name).join(', ') || '—'}</p></div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Guest Info */}
        <motion.div {...fadeInUp}>
          <Card>
            <CardHeader><CardTitle>Guest</CardTitle></CardHeader>
            <CardContent>
              <div className="text-sm space-y-2">
                <p><span className="text-[var(--color-text-muted)]">Email:</span> {booking.guest.email || '—'}</p>
                <p><span className="text-[var(--color-text-muted)]">Phone:</span> {booking.guest.phone || '—'}</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Internal Note */}
        <motion.div {...fadeInUp}>
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle>Internal Note</CardTitle>
              <Button
                size="sm"
                variant="secondary"
                loading={savingNote}
                onClick={() => { setSavingNote(true); bookingActions.update(booking.documentId, { note }).finally(() => setSavingNote(false)) }}
              >
                Save
              </Button>
            </CardHeader>
            <CardContent>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add internal note..."
                className="w-full h-20 bg-[var(--color-bg-elevated)] rounded-[var(--radius-md)] p-3 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] border border-[var(--color-border-default)] focus:outline-none focus:border-[var(--color-accent-amber)]/50 resize-none"
              />
            </CardContent>
          </Card>
        </motion.div>

        {/* Comments */}
        <motion.div {...fadeInUp}>
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Comments</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3 mb-4">
                {(booking.comments ?? []).map((c) => (
                  <div key={c.id} className={`p-3 rounded-[var(--radius-md)] text-sm ${c.role === 'restaurant' ? 'bg-[var(--color-bg-elevated)] ml-4' : 'bg-[var(--color-accent-amber-dim)] mr-4'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant={c.role === 'restaurant' ? 'default' : 'amber'}>{c.role}</Badge>
                      <span className="text-xs text-[var(--color-text-muted)]">{formatRelative(c.createdAt)}</span>
                    </div>
                    <p className="text-[var(--color-text-primary)]">{c.text}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && commentText.trim()) {
                      createComment.mutate({ bookingDocumentId: booking.documentId, text: commentText.trim() }, { onSuccess: () => setCommentText('') })
                    }
                  }}
                  placeholder="Add a comment..."
                  className="flex-1 h-9 px-3 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] border border-[var(--color-border-default)] focus:outline-none focus:border-[var(--color-accent-amber)]/50"
                />
                <Button
                  size="icon-sm"
                  loading={createComment.isPending}
                  onClick={() => {
                    if (!commentText.trim()) return
                    createComment.mutate({ bookingDocumentId: booking.documentId, text: commentText.trim() }, { onSuccess: () => setCommentText('') })
                  }}
                >
                  <Send className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Status Actions */}
        {actions.length > 0 && (
          <motion.div {...fadeInUp}>
            <div className="flex gap-2">
              {actions.map((a) => (
                <Button
                  key={a.targetStatus}
                  variant={a.variant === 'primary' ? 'primary' : a.variant === 'danger' ? 'danger' : 'secondary'}
                  onClick={() => bookingActions.updateStatus(booking.documentId, a.targetStatus)}
                >
                  {a.label}
                </Button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Danger Zone */}
        <motion.div {...fadeInUp}>
          <div className="border border-[var(--color-accent-red)]/20 rounded-[var(--radius-lg)] p-4">
            <h4 className="text-sm font-medium text-[var(--color-accent-red)] mb-2">Danger Zone</h4>
            <Button variant="danger" size="sm" onClick={() => setShowDelete(true)}>
              <Trash2 className="h-3.5 w-3.5" /> Delete Booking
            </Button>
          </div>
        </motion.div>
      </div>

      <BookingForm open={showEdit} onClose={() => setShowEdit(false)} booking={booking} />

      <Modal open={showDelete} onClose={() => setShowDelete(false)} title="Delete Booking?" size="sm">
        <p className="text-sm text-[var(--color-text-secondary)] mb-4">
          This action cannot be undone. The booking for {booking.guest?.name ?? 'Walk-in'} will be permanently removed.
        </p>
        <div className="flex gap-2 justify-end">
          <Button variant="secondary" size="sm" onClick={() => setShowDelete(false)}>Cancel</Button>
          <Button
            variant="danger"
            size="sm"
            loading={deleting}
            onClick={() => { setDeleting(true); bookingActions.remove(booking.documentId).then(() => navigate('/bookings')).finally(() => setDeleting(false)) }}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  )
}
